import { CommonModule, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeEnGB from '@angular/common/locales/en-GB';
import localeEnCA from '@angular/common/locales/en-CA';
import localeFrCA from '@angular/common/locales/fr-CA';
import localeDeAT from '@angular/common/locales/de-AT';
import localeEsUS from '@angular/common/locales/es-US';
import localeEnAU from '@angular/common/locales/en-AU';
import localeFrFR from '@angular/common/locales/fr';
import localeNlNL from '@angular/common/locales/nl';
import localeZhHK from '@angular/common/locales/zh';
import localeEnNZ from '@angular/common/locales/en-NZ';
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbCollapseModule, NgbDropdownModule, NgbModule, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
// import { LocalStorageModule } from 'angular-2-local-storage';
// import { ColorPickerModule } from 'ngx-color-picker';
import { PerfectScrollbarModule, PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { MedicalReportModule } from './actions/medical-report/medical-report.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SYSTEM_PREFERENCES } from './common/system-preferences';
import { UserDefaultInfoComponent } from './components/actions/users/user-default-info/user-default-info.component';
import { UserSettingsDetailsComponent } from './components/actions/users/user-settings-details/user-settings-details.component';
import { LoginComponent } from './components/login/login.component';
import { MenuComponent } from './components/menu/menu.component';
import { applicationInitializer } from './internals/application-initializer';
import { localeIdFactory } from './internals/locale-id.factory';
import { MultiLanguageValidationMessageProvider } from './internals/multi-language-validation-message-provider';
import { systemPreferencesInternalConfig } from './internals/system-preferences-config.internal';
import { TcmApiHttpClientNotificationService } from './internals/tcm-api-http-client-notification.service';
import { TcmApplicationManagerService } from './internals/tcm-application-manager.service';
import { TcmCultureSystemService } from './internals/tcm-culture-system-service';
import { TcmHttpRequestInterceptor } from './internals/tcm-http-request.interceptor';
import { TcmHttpResponseInterceptor } from './internals/tcm-http-response.interceptor';
import { TcmTenantService } from './internals/tcm-tenant.service';
import { TranslationLoaderService } from './internals/translation-loader.service';
import { WafControlsTranslationProvider } from './internals/waf-controls-translation-provider';
import { TcmWafFormConfig } from './internals/waf-form-config';
import { TinyMceModule } from './modules/tinymce.module/tinymce.module';
import { TcmModalService } from './modules/tcm-common/services/tcm-modal.service';
import { TcmCommonSharedModule } from './modules/tcm-common/tcm-common.shared.module';
import { WidgetsModule } from './modules/widgets/widgets.module';
import { FunctionPurePipe } from './pipes/function-pure.pipe';
import { AuthService } from './services/auth.service';
import { AuthenticationStorageService } from './services/system/authentication-storage.service';
import { LoginApiService } from './services/system/login-api.service';
import { LoginComponentService } from './services/system/login-component.service';
import { SetupTranslationSystemService } from './services/system/setup-translation-system.service';
import { TranslationService } from './services/translation.service';
import { TcmWebHubConnectionService } from './internals/tcm-web-hub-connection.service';
import { PlugComponent } from './components/plug/plug.component';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { ErrorService } from './services/system/error.service';
import { ConfigService } from './services/system/config.service';
import { UserSettingsSubscriptionLinkedCollectionComponent } from './components/actions/users/user-settings-subscription-linked-collection/user-settings-subscription-linked-collection';
import { NotificationComponent } from './components/notification/notification.component';
import { TCMFormModule } from './modules/tcm-form/tcm-form.module';
import { TCM_FORM_CONFIG } from './modules/tcm-form/tcm-form-config';
import { ValidationMessageProvider } from './modules/tcm-form/common/validation-message-provider';
import { TCMCoreModule } from './modules/tcm-core.module/tcm-core.module';
import { TranslationProvider } from './modules/tcm-form/common/translation-provider';
import { NotificationsService } from './modules/tcm-core.module/services/notification.service';
import { ApiHttpClient } from './modules/tcm-core.module/services/api-http-client';
import { ApiHttpClientNotificationService } from './modules/tcm-core.module/services/api-http-client-notification.service';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig } from 'ngx-webstorage';
import './extensions/array-extensions';


registerLocaleData(localeEnGB);
registerLocaleData(localeEnCA);
registerLocaleData(localeFrCA);
registerLocaleData(localeDeAT);
registerLocaleData(localeEsUS);
registerLocaleData(localeEnAU);
registerLocaleData(localeFrFR);
registerLocaleData(localeNlNL);
registerLocaleData(localeZhHK);
registerLocaleData(localeEnNZ);


const generalComponents = [
  AppComponent,
  LoginComponent,
  MenuComponent,
  PlugComponent
];

const detailsActionsComponents = [
  UserSettingsDetailsComponent,
  UserDefaultInfoComponent,
  UserSettingsSubscriptionLinkedCollectionComponent,
  NotificationComponent
];

const linkedSearchComponents = [
];

@NgModule({
  declarations: [
        generalComponents,
        detailsActionsComponents,
        // linkedSearchComponents,
        FunctionPurePipe
    ],
    bootstrap: [AppComponent],
    imports: [
        CommonModule,
        BrowserModule,
        FormsModule,
        NgbModule,
        TcmCommonSharedModule,
        // PerfectScrollbarModule, deprecated
        AppRoutingModule,
        BrowserAnimationsModule,
        // ColorPickerModule,
        NgbCollapseModule,
        NgbProgressbarModule,
        NgbDropdownModule,
        ToastrModule.forRoot({
            timeOut: 3000,
            positionClass: 'toast-top-right',
            maxOpened: 3,
            closeButton: true,
            enableHtml: true,
            autoDismiss: true,
        }),
        TranslateModule.forRoot({
            loader: { provide: TranslateLoader, useClass: TranslationLoaderService, deps: [ApiHttpClient] }
        }),
        TinyMceModule,
        MedicalReportModule,
        WidgetsModule,
        ReactiveFormsModule,
        NgxIntlTelInputModule,
        TCMCoreModule.forRoot(),
        TCMFormModule.forRoot()
      ], providers: [
        provideNgxWebstorage(
          withNgxWebstorageConfig({ separator: ':', caseSensitive: true }),
          withLocalStorage(),
        ),
        AuthService,
        AuthenticationStorageService,
        LoginComponentService,
        LoginApiService,
        TcmCultureSystemService,
        { provide: HTTP_INTERCEPTORS, useClass: TcmHttpRequestInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TcmHttpResponseInterceptor, multi: true },
        { provide: PERFECT_SCROLLBAR_CONFIG, useValue: { suppressScrollX: true } },
        { provide: TranslationProvider, useClass: WafControlsTranslationProvider, deps: [TranslationService] },
        { provide: ValidationMessageProvider, useClass: MultiLanguageValidationMessageProvider, deps: [TranslationService] },
        { provide: ApiHttpClientNotificationService, useClass: TcmApiHttpClientNotificationService, deps: [NotificationsService, TcmModalService] },
        { provide: SYSTEM_PREFERENCES, useFactory: () => systemPreferencesInternalConfig },
        { provide: TCM_FORM_CONFIG, useClass: TcmWafFormConfig },
        provideAppInitializer(() => {
        const initializerFn = (applicationInitializer)(inject(ConfigService), inject(LoginApiService), inject(SetupTranslationSystemService), inject(TcmApplicationManagerService), inject(TcmWebHubConnectionService));
        return initializerFn();
      }),
        SetupTranslationSystemService,
        ConfigService,
        TcmTenantService,
        {
            provide: LOCALE_ID,
            useFactory: localeIdFactory,
            deps: [TcmCultureSystemService]
        },
        FunctionPurePipe,
        ErrorService,
        provideHttpClient(withInterceptorsFromDi())
    ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]})
export class AppModule { }
